import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { SunIcon, MoonIcon, ComputerDesktopIcon } from '@heroicons/react/24/outline';

interface ThemeToggleProps {
  className?: string;
}

const ThemeToggle: React.FC<ThemeToggleProps> = ({ className = '' }) => {
  const { theme, setSystemPreference, isUsingSystemPreference, setTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };
  
  const handleSetLightTheme = () => {
    setTheme('light');
    setIsOpen(false);
  };
  
  const handleSetDarkTheme = () => {
    setTheme('dark');
    setIsOpen(false);
  };
  
  const handleUseSystemPreference = () => {
    setSystemPreference();
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={toggleDropdown}
        className={`p-2 rounded-full transition-colors ${
          theme === 'dark' 
            ? 'bg-gray-800 hover:bg-gray-700 text-[#00CC6A]' 
            : 'bg-gray-100 hover:bg-gray-200 text-[#00B058]'
        } ${className}`}
        aria-label="Theme options"
      >
        {isUsingSystemPreference ? (
          <ComputerDesktopIcon className="h-5 w-5" />
        ) : theme === 'dark' ? (
          <MoonIcon className="h-5 w-5" />
        ) : (
          <SunIcon className="h-5 w-5" />
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg z-50 border border-gray-200 dark:border-gray-700">
          <div className="py-1">
            <button
              onClick={handleSetLightTheme}
              className={`flex items-center px-4 py-2 text-sm w-full text-left ${
                theme === 'light' && !isUsingSystemPreference 
                  ? 'bg-gray-100 dark:bg-gray-700 text-[#00B058] dark:text-[#00CC6A]' 
                  : 'text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <SunIcon className="h-5 w-5 mr-2" />
              Light
            </button>
            <button
              onClick={handleSetDarkTheme}
              className={`flex items-center px-4 py-2 text-sm w-full text-left ${
                theme === 'dark' && !isUsingSystemPreference 
                  ? 'bg-gray-100 dark:bg-gray-700 text-[#00B058] dark:text-[#00CC6A]' 
                  : 'text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <MoonIcon className="h-5 w-5 mr-2" />
              Dark
            </button>
            <button
              onClick={handleUseSystemPreference}
              className={`flex items-center px-4 py-2 text-sm w-full text-left ${
                isUsingSystemPreference 
                  ? 'bg-gray-100 dark:bg-gray-700 text-[#00B058] dark:text-[#00CC6A]' 
                  : 'text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <ComputerDesktopIcon className="h-5 w-5 mr-2" />
              System
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThemeToggle; 